import { ArrowUpRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { FeaturedApp } from "@/lib/featured-apps";
import { openExternal } from "@/lib/native";
import { cn } from "@/lib/utils";

const MONOGRAMS: Record<string, string> = {
	bet365: "365",
	betmgm: "MG",
	caesars: "CZ",
	draftkings: "DK",
	"espn-fantasy": "ES",
	fanduel: "FD",
	fanatics: "FN",
	prizepicks: "PP",
	sleeper: "SL",
	underdog: "UD",
	"yahoo-fantasy": "YF",
};

const SIZE_CLASSES = {
	sm: "size-5 rounded-sm text-[10px]",
	md: "size-7 rounded-md text-xs",
	lg: "size-10 rounded-md text-sm",
} as const;

export function AppMark({
	app,
	size = "md",
	className,
}: {
	app: Pick<FeaturedApp, "id" | "name">;
	size?: keyof typeof SIZE_CLASSES;
	className?: string;
}) {
	const monogram =
		MONOGRAMS[app.id] ||
		app.name
			.split(/\s+/)
			.map((part) => part.charAt(0))
			.join("")
			.slice(0, 2)
			.toUpperCase();

	return (
		<span
			aria-hidden
			className={cn(
				"inline-flex shrink-0 items-center justify-center bg-token font-display font-bold text-token-foreground",
				SIZE_CLASSES[size],
				className,
			)}
		>
			{monogram}
		</span>
	);
}

export function AppChip({
	app,
	className,
}: {
	app: Pick<FeaturedApp, "id" | "name">;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 text-sm text-muted-foreground",
				className,
			)}
		>
			<AppMark app={app} size="sm" />
			{app.name}
		</span>
	);
}

export function AppLaunchButton({
	app,
	className,
	primary,
	onOpen,
}: {
	app: FeaturedApp;
	className?: string;
	primary?: boolean;
	onOpen?: (app: FeaturedApp) => void;
}) {
	const open = () => {
		if (onOpen) {
			onOpen(app);
			return;
		}

		const fallback = app.appStoreUrl ?? app.webUrl;

		if (!fallback) {
			return;
		}

		const scheme = app.schemes[0];

		if (scheme) {
			void openExternal(scheme, fallback);
			return;
		}

		window.open(fallback, "_blank", "noopener,noreferrer");
	};

	return (
		<Button
			className={cn("gap-1.5 pl-1.5", className)}
			onClick={open}
			size="sm"
			type="button"
			variant={primary ? "signal" : "outline"}
		>
			<AppMark
				app={app}
				className={primary ? "bg-signal-foreground/15" : undefined}
				size="sm"
			/>
			Open {app.name}
			<ArrowUpRight className="size-3.5 opacity-70" />
		</Button>
	);
}

export function AppPicker({
	apps,
	value,
	onChange,
}: {
	apps: FeaturedApp[];
	value: string[];
	onChange: (value: string[]) => void;
}) {
	return (
		<ToggleGroup
			className="flex flex-wrap gap-1.5"
			data-testid="app-picker"
			onValueChange={onChange}
			type="multiple"
			value={value}
		>
			{apps.map((app) => (
				<ToggleGroupItem
					data-testid={`app-${app.id}`}
					key={app.id}
					value={app.id}
				>
					<AppMark app={app} size="sm" />
					{app.name}
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	);
}
